import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const emptySelection = { type_id: null, size_id: null, pages_id: null, cover_id: null, color_id: null, personalization_id: null };

const uniqueBy = (lines, key) => {
  const seen = new Set();
  const out = [];
  for (const l of lines) {
    if (l[key] && !seen.has(l[key])) {
      seen.add(l[key]);
      out.push(l[key]);
    }
  }
  return out;
};

/** Progressive album configurator: fetches the admin-managed catalog and derives each
 * wizard step's options directly from the actual `album_lines` (only valid combinations
 * are ever offered). Calls onChange(payload|null, price, summary|null) whenever the
 * finalized selection changes, so the parent quote total updates immediately. */
export const useAlbumConfigurator = (onChange) => {
  const [catalog, setCatalog] = useState(null);
  const [sel, setSel] = useState(emptySelection);

  useEffect(() => {
    api.get("/album-catalog").then((r) => setCatalog(r.data));
  }, []);

  const attrById = useMemo(() => {
    const map = {};
    if (!catalog) return map;
    Object.values(catalog.attributes).forEach((list) => list.forEach((a) => (map[a.id] = a)));
    return map;
  }, [catalog]);

  const lines = catalog?.lines || [];
  const linesMatching = (partial) =>
    lines.filter((l) => Object.entries(partial).every(([k, v]) => !v || l[k] === v));

  const typeIds = uniqueBy(lines, "type_id");
  const sizeIds = sel.type_id ? uniqueBy(linesMatching({ type_id: sel.type_id }), "size_id") : [];
  const pagesIds = sel.size_id
    ? uniqueBy(linesMatching({ type_id: sel.type_id, size_id: sel.size_id }), "pages_id").sort(
        (a, b) => Number(attrById[a]?.label) - Number(attrById[b]?.label)
      )
    : [];
  const coverIds = sel.pages_id
    ? uniqueBy(linesMatching({ type_id: sel.type_id, size_id: sel.size_id, pages_id: sel.pages_id }), "cover_id")
    : [];
  const matchedLine = sel.cover_id
    ? linesMatching({ type_id: sel.type_id, size_id: sel.size_id, pages_id: sel.pages_id, cover_id: sel.cover_id })[0] || null
    : null;

  const colorRequired = !!matchedLine?.color_ids?.length;
  const personalizationRequired = !!matchedLine?.personalization_ids?.length;
  const isComplete = !!matchedLine && (!colorRequired || sel.color_id) && (!personalizationRequired || sel.personalization_id);

  const phase = !sel.type_id ? "type"
    : !sel.size_id ? "size"
    : !sel.pages_id ? "pages"
    : !sel.cover_id ? "cover"
    : colorRequired && !sel.color_id ? "color"
    : personalizationRequired && !sel.personalization_id ? "personalization"
    : "summary";

  const selectField = (field, id) => {
    setSel((s) => {
      if (field === "type_id") return { ...emptySelection, type_id: id };
      if (field === "size_id") return { ...emptySelection, type_id: s.type_id, size_id: id };
      if (field === "pages_id") return { ...emptySelection, type_id: s.type_id, size_id: s.size_id, pages_id: id };
      if (field === "cover_id") return { ...emptySelection, type_id: s.type_id, size_id: s.size_id, pages_id: s.pages_id, cover_id: id };
      return { ...s, [field]: id };
    });
  };

  const editFrom = (field) => {
    const order = ["type_id", "size_id", "pages_id", "cover_id", "color_id", "personalization_id"];
    const idx = order.indexOf(field);
    setSel((s) => {
      const next = { ...emptySelection };
      order.slice(0, idx).forEach((k) => (next[k] = s[k]));
      return next;
    });
  };

  const remove = () => setSel(emptySelection);

  const summary = isComplete && matchedLine ? {
    typeLabel: attrById[sel.type_id]?.label, sizeLabel: attrById[sel.size_id]?.label,
    pages: Number(attrById[sel.pages_id]?.label), coverLabel: attrById[sel.cover_id]?.label,
    colorLabel: sel.color_id ? attrById[sel.color_id]?.label : null,
    personalizationLabel: sel.personalization_id ? attrById[sel.personalization_id]?.label : null,
    paperLabel: matchedLine.paper_id ? attrById[matchedLine.paper_id]?.label : null,
    laminateLabel: matchedLine.laminate_id ? attrById[matchedLine.laminate_id]?.label : null,
    featureNotes: matchedLine.feature_notes || [], coverDescription: matchedLine.cover_description || "",
    price: matchedLine.price, imageUrl: matchedLine.image_url,
  } : null;

  useEffect(() => {
    if (summary && matchedLine) {
      onChange({ line_id: matchedLine.id, color_id: sel.color_id || null, personalization_id: sel.personalization_id || null }, matchedLine.price, summary);
    } else {
      onChange(null, 0, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedLine?.id, sel.color_id, sel.personalization_id]);

  const optionsFor = (kind, ids) => ids.map((id) => attrById[id]).filter(Boolean);

  return {
    loading: !catalog, phase, sel, editFrom, remove, summary,
    typeOptions: optionsFor("type", typeIds),
    sizeOptions: optionsFor("size", sizeIds),
    pagesOptions: optionsFor("pages", pagesIds),
    coverOptions: optionsFor("cover", coverIds),
    colorOptions: optionsFor("color", matchedLine?.color_ids || []),
    personalizationOptions: optionsFor("personalization", matchedLine?.personalization_ids || []),
    selectField, attrById,
  };
};
